<template>
  <div :class="'base-create'">
    <el-form :rules="rules" :model="form" class="create-edit-form" ref="ruleForm">
      <el-row :gutter="24" class="form-row">
        <el-col :md="12" :xs="24">
          <elsa-input
            name="name"
            class="required"
            :label="$t('homework.form.assignment_name')"
            :placeholder="$t('study_sets.form.name_your_assignment')"
            v-model="form.name"
          />
        </el-col>
        <el-col :md="12" :xs="24">
          <elsa-tree-select
            name="groupId"
            :label="$t('study_sets.assigned_to')"
            :placeholder="$t($i18nLabelsHelper(currentOrganizationType, 'select_group'))"
            :options="classes"
            :required="false"
            v-model="form.groupIds"
            @clear="onClearClassHandler"
          />
        </el-col>
      </el-row>
      <el-row :gutter="24" class="input-wrapper-assessment">
        <el-col :md="12" :xs="24">
          <elsa-date
            name="startDate"
            placeholder="dd/mm/yy"
            type="date"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            :information="$t('homework.tooltip.start_date')"
            :label="$t('homework.form.start_date')"
            :picker-options="pickerOptions"
            v-model="form.startDate"
            v-bind:class="[{ required: checkRequired }]"
            :disabled="!checkRequired"
          />
        </el-col>
        <el-col :md="12" :xs="24">
          <elsa-select
            name="startTime"
            :options="timeOptionsStart"
            :label="$t('homework.form.start_time')"
            placeholder="00:00"
            v-model.lazy="form.startTime"
            filterable
            clearable
            v-on:change="onStartTimeChange"
            v-bind:class="[{ required: checkRequired }]"
            :disabled="!checkRequired"
          />
        </el-col>
      </el-row>
      <el-row :gutter="24" class="input-wrapper-assessment">
        <el-col :md="12" :xs="24">
          <elsa-date
            name="dueDate"
            placeholder="dd/mm/yy"
            type="date"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            :label="$t('homework.form.due_date')"
            :information="$t('homework.tooltip.due_date')"
            :picker-options="pickerOptions"
            v-model="form.dueDate"
            v-on:change="onEndDateChange"
            v-bind:class="[{ required: checkRequired }]"
            :disabled="!checkRequired"
          />
        </el-col>
        <el-col :md="12" :xs="24">
          <elsa-select
            name="dueTime"
            :options="timeOptionsDue"
            :label="$t('homework.form.due_time')"
            placeholder="00:00"
            v-model.lazy="form.dueTime"
            filterable
            clearable
            v-bind:class="[{ required: checkRequired }]"
            :disabled="!checkRequired"
          />
        </el-col>
      </el-row>
      <el-row :gutter="24" class="form-row">
        <el-col :md="5" :xs="24">
          <span class="set-reminder">{{ $t('homework.set_reminder') }}</span>
          <el-tooltip :content="$t('homework.tooltip.set_reminder')" placement="top-start">
            <i class="el-icon-info" />
          </el-tooltip>
        </el-col>
        <el-col :md="7" :xs="24">
          <hr />
        </el-col>
      </el-row>
      <el-row :gutter="24" class="input-wrapper-reminder">
        <el-col :md="12" :xs="24">
          <elsa-input-number
            controls-position="right"
            :placeholder="$t('homework.form.0_day')"
            style="text-align: center"
            :min="0"
            :max="form.maxReminderBeforeDay"
            :followInfo="$t('homework.form.before_the_due_date')"
            :formatter="reminderBeforeDayFormatter"
            :invertFormatter="reminderBeforeDayInvertFormatter"
            v-model="form.reminderBeforeDay"
            :disabled="!checkRequired"
          />
        </el-col>
        <el-col :md="6" :xs="24">
          <elsa-time
            name="timeNotify"
            class="first-assessment elsa-input"
            placeholder="00:00"
            format="HH:mm A"
            value-format="hh:mm"
            :picker-options="timeOptions"
            v-model="form.timeNotify"
            :disabled="!checkRequired"
          />
        </el-col>
      </el-row>
      <el-row :gutter="24" class="input-wrapper-assessment">
        <el-col :md="12" :xs="24">
          <elsa-select
            name="shareable"
            :information="$t('homework.form.asignment_shareable_tooltip')"
            :placeholder="$t('homework.form.shareable')"
            :options="shareableOptions"
            :label="$t('homework.form.shareable')"
            v-model.lazy="form.shareable"
            filterable
            clearable
          />
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import ElsaInput from '@/components/common/Input';
import ElsaInputNumber from '@/components/common/InputNumber';
import ElsaDate from '@/components/common/dateInput';
import ElsaTime from '@/components/common/timeInput';
import { getHours, getMinutes } from 'date-fns'
import { mapGetters, mapActions } from 'vuex';
import ElsaSelect from '@/components/common/Select';
import ElsaTreeSelect from "@/components/common/TreeSelect";
import { pathOr } from 'ramda';
import { handleFormValidation } from '@/helpers/formValidationHelper';
import {generateTimeOption} from "@/helpers/timeHelper";
import classes from '@/store/modules/classes';

export default {
  components: {
    ElsaInput,
    ElsaInputNumber,
    ElsaSelect,
    ElsaDate,
    ElsaTime,
    ElsaTreeSelect
  },
  props: {
    index: {
      type: Number,
      default: 0,
    },
    defaultDateReminder: {
      type: Number,
      default: 5,
    },
    currentStep: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      form: {
        name: null,
        groupIds: [],
        startDate: null,
        startTime: null,
        dueDate: null,
        dueTime: null,
        reminderBeforeDay: 1,
        timeNotify: null,
        maxReminderBeforeDay: 1,
        assign_now: false,
        shareable: null,
      },
      rules: {
        name: { required: true, type: 'string', message: this.$t('homework.errors.please_provide_name') },
        // groupId: { validator: validate, trigger: 'blur' },
        startDate: { validator: this.customValidator },
        dueDate: { validator: this.customValidator },
        startTime: { validator: this.customValidator },
        dueTime: { validator: this.customValidator },
      },
      modals: {
        showInviteTeacherModal: false,
      },
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 86400000;
        },
      },
      loadingClasses: false,
      classes: [],
      admins: [],
      timeOptionsStart: [],
      timeOptionsDue: [],
      timeOptions: {
        start: '00:00',
        step: '00:15',
        end: '23:30'
      },
      shareableOptions: [
        {value: "branch_only", label: "Branch"},
        {value: "organization", label: "My Organization"},
        {value: "class", label: "Class Only"},
      ],
      selectAllVal: false,
      classList: []
    };
  },
  computed: {
    ...mapGetters({
      buildingAssignments: 'homeworks/getBuildingAssignment',
      currentOrganizationType: 'organization/currentOrganizationType',
      isElsaAdmin: 'user/isElsaAdmin',
      isAdminOfAnOrgHasBranches: 'organization/isAdminOfAnOrgHasBranches',
    }),
    assignments() {
      return pathOr([], ['buildingAssignments', 'assignments'])(this);
    },
    assignmentDetails() {
      if (this.assignments && this.assignments.length > this.index) {
        return this.assignments[this.index];
      }
      return null;
    },
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    dateObject() {
      return {
        start: this.classDetails.startDate ? new Date(this.classDetails.startDate) : '',
        end: this.classDetails.endDate ? new Date(this.classDetails.endDate) : '',
      };
    },
    checkRequired() {
      return this.form.groupIds && this.form.groupIds !== '';
    },
  },
  async mounted() {
    this.timeOptionsStart = this.timeOptionsDue = generateTimeOption();
    if(this.isElsaAdmin) {
      this.shareableOptions.push({value: "public", label: "Public"});
    }
    try {
      this.searchGroup('');
      if (this.assignmentDetails && this.assignmentDetails.name !== '') {
        this.form = this.assignmentDetails;
      }
    } catch (error) {
      // console.error(error)
    }
  },
  methods: {
    ...mapActions({
      updateBuildingAssignment: 'homeworks/updateBuildingAssignment',
    }),
    reset() {
      this.loadingClasses = false;
      this.classes = [];
      this.admins = [];
    },
    reminderBeforeDayFormatter(v) {
      if (this.form.dueDate === '' || !this.form.reminderBeforeDay) {
        return `0 ${this.$t('common.day')}`;
      } else {
        if (v > 1) {
          return `${v} ${this.$t('common.days')}`;
        } else {
          return `${v} ${this.$t('common.day')}`;
        }
      }
    },
    reminderBeforeDayInvertFormatter(v) {
      return v.trim().split(' ')[0];
    },
    validateForm(callback) {
      this.updateBuildingAssignment({
        assignments: [this.form],
      });
      handleFormValidation(this.$refs.ruleForm, callback, () => {});
    },
    validFormCallback() {
      this.$emit('next-step');
    },
    handleContinueClick() {
      this.validateForm(this.validFormCallback);
    },
    handleCancelClick() {
      return this.$router.push({
        name: 'classes',
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    onEndDateChange() {
      if (this.form.dueDate && this.form.startDate) {
        let first = Date.parse(this.form.startDate);
        let second = Date.parse(this.form.dueDate);
        this.form.reminderBeforeDay = this.form.maxReminderBeforeDay = Math.round((second - first) / (1000 * 60 * 60 * 24)) - 1;
      }
    },
    onStartTimeChange() {
      if(this.form.startDate && this.form.startTime == "now") {
        this.form.assign_now = true;
      }
    },
    async remoteSearchGroups(query) {
      if (query !== '') {
        this.loadingClasses = true;
        try {
          await this.searchGroup(query);
        } catch (err) {
          // console.error(err)
        } finally {
          this.loadingClasses = false;
        }
      } else {
        this.options = [];
      }
    },
    searchGroup(search) {
      return this.getGroup({ search });
    },
    async getGroup({ page = 1, search = null, status = "" }) {
      this.loadingClasses = true;
      const organizationId = this.organizationId;
      let params = { page, search, status };
      let url = `/v1/organizations/${organizationId}/all-groups`

      if (this.isAdminOfAnOrgHasBranches) {
        url = `/v1/organizations/${organizationId}/all-classes/grouped`
      }

      const { data } = await this.$axios.get(url, { params });

      this.loadingClasses = false;

      if (!this.isAdminOfAnOrgHasBranches) {
        this.classes = data.map(x => {
          return { label: x.name, id: x.id };
        });

        return
      }

      const { parent, branches } = data
      let parentClasses = []
      let branchClasses = []

      if (parent) {
        parentClasses = parent.classes.map((cls) => {
          return {
            id: cls.id,
            label: cls.name,
          }
        })
      }

      if (branches) {
        branchClasses = branches.map((branch) => {
          return {
            id: branch.id,
            label: branch.name,
            children: branch.classes.map(cls => ({
              id: cls.id,
              label: cls.name,
            })
          )}
        })
      }

      this.classes = [ ...parentClasses, ...branchClasses ]
    },
    handleGroupSelect(val) {
      if (val === '') {
        const name = this.form.name;
        this.$refs.ruleForm.clearValidate();
        this.$refs.ruleForm.resetFields();
        this.form.name = name;
      }
    },
    getMessageByField(name) {
      if (name === 'startDate') return this.$t('homework.errors.please_provide_start_on');
      else if (name === 'dueDate') return this.$t('homework.errors.please_provide_due_on');
      else if (name === 'startTime') return this.$t('homework.errors.please_provide_start_time');
      else if (name === 'dueTime') return this.$t('homework.errors.please_provide_due_time');
    },
    customValidator(rule, value, callback) {
      const groupIds = this.form.groupIds;
      if (!groupIds || groupIds === '') {
        callback();
      } else if (!value || value === '') {
        callback(new Error(this.getMessageByField(rule.field)));
      } else {
        callback();
      }
    },
    onClearClassHandler() {
      this.form.groupIds = []
    }
  },
  watch: {
    'form.startDate'(new_value) {
      if((Date.parse(new Date()) - Date.parse(new_value)) > 0) {
        const hourAddition = Math.ceil(getMinutes(Date.now())/15)/4;
        const assign_now_op = true;
        this.timeOptionsStart = generateTimeOption(getHours(Date.now()) + hourAddition, assign_now_op);
      } else {
        this.timeOptionsStart = generateTimeOption(0);
      }
    },
    'form.dueDate'(new_value) {
      if((Date.parse(new Date()) - Date.parse(new_value)) > 0) {
        const hourAddition = Math.ceil(getMinutes(Date.now())/15)/4;
        this.timeOptionsDue = generateTimeOption(getHours(Date.now()) + hourAddition);
      } else {
        this.timeOptionsDue = generateTimeOption(0);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.remove-assignment {
  position: absolute;
  right: 0px;
  top: -20px;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
}

.set-reminder {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #121131;
}

.base-create {
  padding: 0px 0px;
  position: relative;
}

.pick-teacher {
  font-weight: 600;
  font-size: 14px;
  color: #478dff;
  flex: 1;

  .invite-teacher {
    cursor: pointer;
    margin-top: 16px;
    float: left;
  }
}

.input-wrapper-reminder {
  margin-top: 24px;

  &::v-deep .el-icon-time:before {
    content: 'at';
    font-size: 16px;
    line-height: 24px;
    font-family: 'SF Pro Display';
  }
}

hr {
  border: 1px solid #e5e5ea;
  margin-top: 12px;
}

.create-edit-form .form-row .el-form-item.elsa-input:not(:last-of-type) {
  margin-right: 0;
}

::v-deep .elsa-section {
  background-color: #f2f7fa;
}

::v-deep .el-date-editor.el-input {
  width: 200px;
}

::v-deep .elsa-input .el-input .el-input__inner {
  width: 100%;
}

::v-deep .el-date-editor.el-input {
  width: 100% !important;
}

::v-deep .el-form {
  padding-bottom: 0px;
}

::v-deep .el-input-number .el-input input {
  text-align: left;
  text-transform: lowercase;
}

::v-deep .el-form .el-form-item .el-form-item__label {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #121131;
}

::v-deep .required .el-form-item__label::after {
  content: '*';
  margin-left: 2px;
  color: #eb5757;
}

::v-deep .el-form-item__label::before {
  content: '';
}

::v-deep .el-form .el-form-item {
  margin-bottom: 32px;
}

.elsa-input {
  &::v-deep .el-input .el-input__inner {
    height: 48px !important;
  }
}

::v-deep .el-input__icon {
  line-height: 50px !important;
}

::v-deep .el-icon-info:before {
  color: #8e8e93;
  margin-left: 8px;
}

::v-deep .input-wrapper-reminder .el-form-item__label:after {
  content: unset;
}
</style>
